import React, { FC, useReducer, useState } from 'react'
import { codeSelectorDefaultOptions } from '../code-selector/assets'
import { checkoutSimulatorSnippets } from '../code-selector/code-snippets/checkout'
import { CodeSelectorOption } from '../code-selector/interfaces'
import { CheckoutSimulatorContext } from './CheckoutSimulatorContext'
import { checkoutSimulatorReducer } from './checkoutSimulatorReducer'
import {
  CheckoutSimulatorParametersProps,
  CheckoutSimulatorState,
} from './interfaces'

const initialState: CheckoutSimulatorState = {
  snippetSchema: checkoutSimulatorSnippets,
  options: codeSelectorDefaultOptions,
  integrationType: 'standard',
  snippets: {},
  parameters: {
    clientId: 'test',
    appSecret: '<test_secret>',
    buttons: [
      'payment-buttons_paypal',
      'payment-buttons_paylater',
      'payment-buttons_venmo',
      'payment-buttons_3ds',
      'payment-buttons_card',
    ],
    paymentModel: 'onetime-payment',
    buttonStyle: 'rect',
    buttonLayout: 'vertical',
  },
}

export const CheckoutSimulatorProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(checkoutSimulatorReducer, initialState)

  const setOptions = (options: CodeSelectorOption[]) => {
    dispatch({
      type: '[Checkout_Simulator] Set buttons options',
      payload: options,
    })
  }

  const setOptionValue = (name: string, value: string) => {
    dispatch({
      type: '[Checkout_Simulator] Set option value',
      payload: {
        name,
        value,
      },
    })
  }

  const setIntegrationType = (integrationType: string) => {
    dispatch({
      type: '[Checkout_Simulator] Set integration type',
      payload: {
        integrationType,
      },
    })
  }

  const setParameters = (parameters: CheckoutSimulatorParametersProps) => {
    dispatch({
      type: '[Checkout_Simulator] Set parameters',
      payload: parameters,
    })
  }

  const setParameter = (name: string, value: any) => {
    dispatch({
      type: '[Checkout_Simulator] Set parameter',
      payload: {
        name,
        value,
      },
    })
  }

  const updateSnippets = () => {
    const integrationType = state.options.find(
      (option) => option.name === 'integration',
    )?.value

    const schema = state.snippetSchema.find(
      (schema) => schema.name === integrationType,
    )

    const environmentLanguages = schema?.environments.map((environment) => ({
      environment: environment.name,
      language: state.options.find((option) => option.name === environment.name)
        ?.value,
    }))

    const environmentLanguageFiles = environmentLanguages?.map(
      (environmentLanguage) => ({
        environment: environmentLanguage.environment,
        files: schema?.environments
          .find(
            (environment) =>
              environment.name === environmentLanguage.environment,
          )
          ?.samples.find(
            (sample) => sample.language === environmentLanguage.language,
          )
          ?.files.filter(
            (file) =>
              file &&
              (!file.conditions ||
                file.conditions.filter((condition) => {
                  if (condition.option) {
                    let conditionOption = state.options.find(
                      (conditionOption) =>
                        conditionOption.name === condition.option,
                    )
                    if (condition.type === 'different')
                      return conditionOption?.value !== condition.value
                    return conditionOption?.value === condition.value
                  } else if (condition.parameter) {
                    let conditionParameter =
                      state.parameters[condition.parameter]
                    switch (condition.type) {
                      case 'includes':
                        if (!Array.isArray(conditionParameter)) return false
                        return conditionParameter.includes(condition.value)
                      case 'not_includes':
                        if (!Array.isArray(conditionParameter)) return false
                        return !conditionParameter.includes(condition.value)
                      case 'different':
                        return conditionParameter !== condition.value
                      default:
                        return conditionParameter === condition.value
                    }
                  }
                }).length === file.conditions.length),
          ),
      }),
    )

    const snippets = environmentLanguageFiles?.reduce(
      (files: any, environmentFile: any) => {
        return {
          ...files,
          [environmentFile.environment]: environmentFile.files,
        }
      },
      {},
    )

    dispatch({
      type: '[Checkout_Simulator] Set snippets',
      payload: snippets,
    })
  }

  return (
    <CheckoutSimulatorContext.Provider
      value={{
        ...state,
        setOptions,
        setOptionValue,
        setIntegrationType,
        setParameters,
        setParameter,
        updateSnippets,
      }}
    >
      {children}
    </CheckoutSimulatorContext.Provider>
  )
}
